import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { http } from "@/lib/api";
import PublicSubmissionForm from "@/components/PublicSubmissionForm";
import { useCountryFilter } from "@/lib/useCountryFilter";
import CountryFilterBanner from "@/components/CountryFilterBanner";

export default function Housing() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("browse");
  const filtered = useCountryFilter(items, ["country", "city"]);
  useEffect(() => { http.get("/housing-projects").then((r) => setItems(r.data)); }, []);

  return (
    <div data-testid="housing-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <div className="overline mb-3">HOUSING PROJECTS</div>
      <h1 className="font-display text-4xl md:text-5xl max-w-2xl">Every home tracked. Every family known.</h1>
      <p className="mt-6 max-w-xl text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
        Sponsor a build during your lifetime. Photos, progress, cost, families helped, sponsors — full transparency.
      </p>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b" style={{ borderColor: "var(--wofy-border)" }}>
        <button data-testid="housing-tab-browse" onClick={() => setTab("browse")} className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors ${tab === "browse" ? "border-b-2" : "opacity-60 hover:opacity-100"}`} style={tab === "browse" ? { borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" } : {}}>Browse projects</button>
        <button data-testid="housing-tab-submit" onClick={() => setTab("submit")} className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors ${tab === "submit" ? "border-b-2" : "opacity-60 hover:opacity-100"}`} style={tab === "submit" ? { borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" } : {}}>Suggest / Submit a Housing Project</button>
      </div>

      {tab === "submit" ? (
        <div className="mt-10 max-w-3xl">
          <PublicSubmissionForm type="housing_project" />
        </div>
      ) : (
        <>

      {/* Featured editorial: Habitat for Humanity */}
      <Link
        to="/housing/habitat-for-humanity"
        data-testid="housing-featured-habitat"
        className="group mt-14 block wofy-card !p-0 overflow-hidden relative"
      >
        <div className="grid md:grid-cols-2">
          <div className="aspect-[4/3] md:aspect-auto md:min-h-[380px] overflow-hidden">
            <img
              src=""
              alt="Volunteers building a home together"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <div className="flex items-center gap-2">
              <span className="wofy-tag" style={{ background: "var(--wofy-primary)", color: "#FDFBF7" }}>FEATURED</span>
              <span className="overline">GLOBAL HOUSING</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl mt-4 leading-[1.1]">
              Habitat for Humanity: Building Homes, Communities, and Hope
            </h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
              Since 1976 Habitat has helped more than <b style={{ color: "var(--wofy-text)" }}>65 million people</b> build or improve a place they can call home. A partnership model that redefines what affordable housing can look like — and lessons for how Social Pulse operates today.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium group-hover:translate-x-1 transition-transform" style={{ color: "var(--wofy-primary)" }}>
              Read the full article <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>

      {items.length > 0 && (
        <div className="mt-14 overline">COMMUNITY PROJECTS</div>
      )}

      <CountryFilterBanner shown={filtered.length} total={items.length} noun="housing projects" />

      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((h) => (
          <Link key={h.id} to={`/housing/${h.id}`} data-testid={`housing-${h.id}`} className="wofy-card !p-0 overflow-hidden group">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={h.image_url} alt={h.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="overline">{h.city.toUpperCase()}, {h.country.toUpperCase()}</div>
                <span className="font-mono text-xs">{h.progress}%</span>
              </div>
              <div className="font-display text-xl mt-2">{h.title}</div>
              <p className="text-sm mt-2 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>{h.description}</p>
              <div className="mt-4 h-1.5 w-full rounded-full" style={{ background: "var(--wofy-surface)" }}>
                <div className="h-full rounded-full" style={{ width: `${h.progress}%`, background: "var(--wofy-primary)" }} />
              </div>
              <div className="mt-3 flex justify-between text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>
                <span>${h.raised.toLocaleString()} raised</span>
                <span>of ${h.cost.toLocaleString()}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
        </>
      )}
    </div>
  );
}
